import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { HiX, HiSave, HiOutlineLink } from "react-icons/hi";
import { editarLicitacion, obtenerLicitacion } from "../api";
import { toast } from "react-toastify";
import "../styles.css";

function ModalEditarLicitacion({ numero, onClose, onActualizar }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    async function fetchDatos() {
      if (!numero) return;
      try {
        const lic = await obtenerLicitacion(numero);

        const fechaFormateada =
          lic.fecha_presentacion && lic.fecha_presentacion.includes("T")
            ? lic.fecha_presentacion.split("T")[0]
            : lic.fecha_presentacion || "";

        let enlacesTexto = "";
        if (typeof lic.enlaces === "string" && lic.enlaces.trim()) {
          enlacesTexto = lic.enlaces
            .split("|")
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
            .join("\n");
        } else if (Array.isArray(lic.enlaces)) {
          enlacesTexto = lic.enlaces.join("\n");
        }

        setDatos({
          boe: lic.boe || "",
          titulo: lic.titulo || "",
          ambito: lic.ambito || "",
          presupuesto: lic.presupuesto?.toString() || "",
          observaciones: lic.observaciones || "",
          clasificacion: lic.clasificacion || "",
          procedimiento: lic.procedimiento || "",
          organismo: lic.organismo || "",
          fecha_presentacion: fechaFormateada,
          estado: lic.estado || "pendiente",
          medio: lic.medio || "",
          estudio_economico: lic.estudio_economico || "",
          resultado_apertura: lic.resultado_apertura || "",
          pais: lic.pais || "",
          enlaces: enlacesTexto
        });
      } catch (err) {
        console.error("Error al cargar licitación:", err);
      } finally {
        setCargando(false);
      }
    }
    fetchDatos();
  }, [numero]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDatos((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const enlacesNorm = (datos.enlaces || "")
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .map((l) => {
          const [nombreRaw, urlRaw] = l.split(";", 2);
          const nombre = (nombreRaw || "").trim();
          const url = (urlRaw || "").trim();
          if (!url) return null;
          const nombreSafe = nombre.replace("|", " ").replace(";", ",");
          return `${nombreSafe};${url}`;
        })
        .filter(Boolean)
        .join("|");

      const payload = {
        ...datos,
        presupuesto: datos.presupuesto ? parseFloat(datos.presupuesto) : null,
        fecha_presentacion: datos.fecha_presentacion || null,
        pais: datos.pais || null,
        enlaces: enlacesNorm
      };

      await editarLicitacion(numero, payload);

      if (typeof onActualizar === "function") {
        onActualizar({ ...payload, numero_expediente: numero });
      }

      toast.success("Licitación actualizada");
      onClose();
    } catch (err) {
      console.error("Error al actualizar licitación:", err);
      toast.error("Error al actualizar la licitación.");
    }
  };

  if (cargando) {
    const loadingContent = (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: '400px', textAlign: 'center' }}>
          <div className="loader" style={{ margin: '20px auto' }}></div>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
    return ReactDOM.createPortal(loadingContent, document.body);
  }

  if (!datos) return null;

  const modalContent = (
    <div className="modal-overlay">
      <div className="modal animate-fadeIn" style={{ maxWidth: '900px' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0 }}>Editar Licitación <span style={{ color: 'var(--accent-primary)' }}>#{numero}</span></h2>
          <button className="btn-icon" onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <HiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="form-licitacion-modern">
          <div className="info-grid">
            <div className="form-group full-width">
              <label>Título *</label>
              <input
                name="titulo"
                type="text"
                value={datos.titulo}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Número expediente</label>
              <input type="text" value={numero} disabled style={{ opacity: 0.6 }} />
            </div>

            <div className="form-group">
              <label>Fecha BOE</label>
              <input
                name="boe"
                type="date"
                value={datos.boe}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Organismo</label>
              <input
                name="organismo"
                type="text"
                value={datos.organismo}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Presupuesto (Sin IVA)</label>
              <div className="input-with-icon" style={{ position: 'relative' }}>
                <input
                  name="presupuesto"
                  type="number"
                  step="0.01"
                  value={datos.presupuesto}
                  onChange={handleChange}
                  style={{ paddingRight: '40px' }}
                />
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }}>€</span>
              </div>
            </div>

            <div className="form-group">
              <label>País</label>
              <input
                name="pais"
                type="text"
                value={datos.pais}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Ámbito</label>
              <input
                name="ambito"
                type="text"
                value={datos.ambito}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Fecha presentación</label>
              <input
                name="fecha_presentacion"
                type="date"
                value={datos.fecha_presentacion}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Estado</label>
              <select
                name="estado"
                value={datos.estado}
                onChange={handleChange}
              >
                <option value="pendiente">En curso</option>
                <option value="presentada">Presentada</option>
                <option value="proxima">Próxima</option>
                <option value="cerrada">Cerrada</option>
                <option value="adjudicada">Adjudicada</option>
                <option value="no adjudicada">No adjudicada</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Observaciones / Garantía</label>
              <textarea
                name="observaciones"
                value={datos.observaciones}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="form-group full-width">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <HiOutlineLink /> Enlaces (Nombre;URL, uno por línea)
              </label>
              <textarea
                name="enlaces"
                value={datos.enlaces}
                onChange={handleChange}
                rows={4}
              />
            </div>
          </div>

          <div className="modal-footer" style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button type="button" className="btn-gestion" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-nueva">
              <HiSave /> Guardar Cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default ModalEditarLicitacion;
